import React from 'react';
import '../../../styles/CampaignWizard.css';

const ScheduleCampaign = ({ schedule, onChange }) => {
  const handleChange = (field, value) => {
    onChange({
      ...schedule,
      [field]: value
    });
  };
  
  const handleTimeChange = (field, value) => {
    onChange({
      ...schedule,
      sendingHours: {
        ...schedule.sendingHours,
        [field]: value
      }
    });
  };
  
  const handleDayToggle = (day) => {
    const updatedDays = [...schedule.sendingDays];
    if (updatedDays.includes(day)) {
      // Remove day
      const index = updatedDays.indexOf(day);
      updatedDays.splice(index, 1);
    } else {
      // Add day
      updatedDays.push(day);
    }
    
    onChange({
      ...schedule,
      sendingDays: updatedDays
    });
  };
  
  // Get timezone options
  const getTimezoneOptions = () => {
    const timezones = [
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Phoenix',
      'America/Anchorage',
      'America/Honolulu',
      'America/Toronto',
      'America/Vancouver',
      'Europe/London',
      'Europe/Paris',
      'Europe/Berlin',
      'Europe/Moscow',
      'Asia/Tokyo',
      'Asia/Shanghai',
      'Asia/Dubai',
      'Asia/Singapore',
      'Australia/Sydney',
      'Pacific/Auckland'
    ];
    
    return timezones.map(tz => (
      <option key={tz} value={tz}>
        {tz.replace('_', ' ').replace('/', ': ')}
      </option>
    ));
  };
  
  // Days of the week
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  return (
    <div className="wizard-step-container">
      <div className="wizard-step-header">
        <h2>Schedule Campaign</h2>
        <p>Set when your emails will be sent to maximize engagement.</p>
      </div>
      
      <div className="schedule-container">
        <div className="schedule-section">
          <h3>Campaign Duration</h3>
          <div className="form-hint">Specify the start and optional end date for your campaign.</div>
          
          <div className="date-inputs">
            <div className="date-input-group">
              <div className="form-group">
                <label htmlFor="startDate">Start Date *</label>
                <input
                  type="date"
                  id="startDate"
                  value={schedule.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
                <div className="form-hint">When should the campaign start sending emails?</div>
              </div>
            </div>
            
            <div className="date-input-group">
              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  value={schedule.endDate || ''}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  min={schedule.startDate}
                />
                <div className="form-hint">Optional: When should the campaign stop? Leave blank for no end date.</div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="schedule-section">
          <h3>Sending Time</h3>
          <div className="form-hint">Configure the hours when emails can be sent.</div>
          
          <div className="time-settings">
            <div className="time-input-group">
              <div className="form-group">
                <label htmlFor="fromTime">From</label>
                <input
                  type="time"
                  id="fromTime"
                  value={schedule.sendingHours.from}
                  onChange={(e) => handleTimeChange('from', e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div className="time-input-group">
              <div className="form-group">
                <label htmlFor="toTime">To</label>
                <input
                  type="time"
                  id="toTime"
                  value={schedule.sendingHours.to}
                  onChange={(e) => handleTimeChange('to', e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div className="timezone-selector">
              <div className="form-group">
                <label htmlFor="timezone">Timezone</label>
                <select
                  id="timezone"
                  value={schedule.sendingHours.timezone}
                  onChange={(e) => handleTimeChange('timezone', e.target.value)}
                  required
                >
                  {getTimezoneOptions()}
                </select>
              </div>
            </div>
          </div>
        </div>
        
        <div className="schedule-section">
          <h3>Sending Days</h3>
          <div className="form-hint">Select days of the week when emails can be sent.</div>
          
          <div className="days-selector">
            {days.map((day) => (
              <div key={day} className="form-group day-option">
                <input
                  type="checkbox"
                  id={`day-${day}`}
                  checked={schedule.sendingDays.includes(day)}
                  onChange={() => handleDayToggle(day)}
                />
                <label htmlFor={`day-${day}`}>{day}</label>
              </div>
            ))}
          </div>
          <div className="form-hint">Note: No emails will be sent on days that are not selected.</div>
        </div>
        
        <div className="schedule-section">
          <h3>Sending Volume</h3>
          <div className="form-hint">Configure how many emails can be sent per day.</div>
          
          <div className="form-group">
            <label htmlFor="dailyLimit">Daily Email Limit</label>
            <input
              type="number"
              id="dailyLimit"
              min="1"
              value={schedule.dailyLimit || 100}
              onChange={(e) => handleChange('dailyLimit', parseInt(e.target.value) || 100)}
              required
            />
            <div className="form-hint">Maximum number of emails to send per day.</div>
          </div>
          
          <div className="form-group">
            <label htmlFor="throttle">Throttle (emails per hour)</label>
            <input
              type="number"
              id="throttle"
              min="1"
              value={schedule.throttle || 25}
              onChange={(e) => handleChange('throttle', parseInt(e.target.value) || 25)}
              required
            />
            <div className="form-hint">Limit the number of emails sent per hour to avoid rate limiting.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleCampaign;